/**
 * RecipientPicker — Mehrfachauswahl der Briefempfänger.
 *
 * 2026-08 · Erweiterung:
 *   - Firmen-Chips zeigen zusätzlich ein Dropdown der verknüpften
 *     Ansprechpartner (contact_persons). Der primär markierte Ansprechpartner
 *     wird per Default als Empfänger vorgewählt und liefert eine 2-Zeilen-
 *     Anrede ("Sehr geehrte Damen und Herren," + "sehr geehrter Herr X,").
 *   - Neben dem Dropdown gibt es einen "z. Hd."-Toggle. Wenn aktiv, erscheint
 *     im Adressfenster unter der Firma "z. Hd. <Name>". Genau ein
 *     Ansprechpartner pro Firma darf den z.-Hd.-Toggle gesetzt haben.
 *
 * Keys nach außen (`selected` / `onChange`):
 *   "<contact_id>"                            → einfacher Empfänger
 *   "<contact_id>:cp:<person_id>"             → verknüpfter Ansprechpartner
 *   "<contact_id>:cp:<person_id>:zhd"         → verknüpfter Ansprechpartner + z. Hd.
 *   "<contact_id>:rep"                        → Legacy-Freitext (falls noch vorhanden)
 */
import { User, Buildings, UserCircle, HouseLine } from "@phosphor-icons/react";
import { useMemo } from "react";

// Hilfen -----------------------------------------------------------------
const parseKey = (k) => {
  const parts = (k || "").split(":");
  return {
    contact_id: parts[0] || "",
    mode: parts[1] || "",           // "", "cp", "rep"
    person_id: parts[1] === "cp" ? (parts[2] || "") : "",
    zhd: parts[1] === "cp" && parts[3] === "zhd",
    raw: k,
  };
};

const makeKey = (contact_id, person_id, zhd) => {
  if (!person_id) return contact_id;
  return zhd ? `${contact_id}:cp:${person_id}:zhd` : `${contact_id}:cp:${person_id}`;
};

export default function RecipientPicker({
  candidates, selected, onChange, className = "",
}) {
  // Gruppiere Kandidaten nach contact_id → { base, persons: [...] }
  const groups = useMemo(() => {
    const map = new Map();
    (candidates || []).forEach((c) => {
      const cid = c.contact_id || c.key.split(":")[0];
      if (!map.has(cid)) map.set(cid, { base: null, persons: [], reps: [] });
      const g = map.get(cid);
      if (c.type === "contact_person") g.persons.push(c);
      else if (c.type === "rep") g.reps.push(c);
      else g.base = c;
    });
    return Array.from(map.values());
  }, [candidates]);

  if (!candidates || candidates.length === 0) {
    return (
      <div className={`text-[11px] text-slate-400 italic ${className}`}>
        Keine Empfänger gefunden.
      </div>
    );
  }

  const isSel = (k) => selected.includes(k);
  // z. Hd.-Keys ("…:zhd") sind keine eigenen Kandidaten → beim Filtern gegen den Basis-Key prüfen
  const setSel = (nextSet) => onChange(candidates.map((c) => c.key)
    .map((k) => (nextSet.has(k) ? k : nextSet.has(`${k}:zhd`) ? `${k}:zhd` : null)).filter(Boolean));

  // Toggle-Verhalten für Firmen/Personen-Chips
  // 2026-10 — Wenn Basis-Chip getoggelt wird, gleichzeitig alle cp-Keys der
  // gleichen Firma entfernen (verhindert doppelte Adress-Blöcke).
  const toggleKey = (key) => {
    const s = new Set(selected);
    if (s.has(key)) {
      s.delete(key);
    } else {
      // Vor dem Aktivieren der Firma → cp-Keys derselben Firma entfernen
      const cid = key.split(":")[0];
      [...s].forEach((k) => {
        const p = parseKey(k);
        if (p.contact_id === cid && p.mode === "cp") s.delete(k);
      });
      s.add(key);
    }
    setSel(s);
  };

  // Ansprechpartner-Auswahl im Dropdown pro Firma-Gruppe:
  // - Value = "none" (nur Firma), "<person_id>" (Firma + primär/gewählter AP)
  // - Beim Umschalten wird der bestehende cp-Key des Kontakts ersetzt (z. Hd. bleibt erhalten,
  //   sofern derselbe AP weiterhin aktiv ist).
  const setContactPerson = (contact_id, person_id) => {
    const s = new Set(selected);
    // Entferne alle bestehenden Keys dieses Kontakts (base + cp)
    [...s].forEach((k) => {
      const p = parseKey(k);
      if (p.contact_id === contact_id) s.delete(k);
    });
    if (!person_id) {
      // Kein AP → nur Firma
      s.add(contact_id);
    } else {
      // Firma nicht mehr als eigenständigen Empfänger; nur den AP-Key
      s.add(makeKey(contact_id, person_id, false));
    }
    setSel(s);
  };

  // z.-Hd.-Checkbox pro Firma (wirkt auf den derzeit aktiven AP dieser Firma).
  const toggleZhd = (contact_id) => {
    const s = new Set(selected);
    const activeCp = [...s].map(parseKey).find((p) => p.contact_id === contact_id && p.mode === "cp");
    if (!activeCp) return;  // z.Hd. ohne AP nicht möglich
    s.delete(activeCp.raw);
    s.add(makeKey(contact_id, activeCp.person_id, !activeCp.zhd));
    setSel(s);
  };

  const iconFor = (type, role) => {
    if (role === "owner") return <HouseLine size={13} className="text-amber-600" />;
    if (type === "company") return <Buildings size={13} className="text-slate-500" />;
    if (type === "rep" || type === "contact_person") return <UserCircle size={13} className="text-emerald-600" />;
    return <User size={13} className="text-slate-500" />;
  };

  return (
    <div className={`space-y-2 ${className}`} data-testid="msg-recipient-picker">
      {groups.map((g, idx) => {
        const base = g.base;
        if (!base) return null;
        const activeCp = selected
          .map(parseKey)
          .find((p) => p.contact_id === base.contact_id && p.mode === "cp");
        const activePersonId = activeCp?.person_id || "";
        const zhdActive = !!activeCp?.zhd;
        const hasPersons = g.persons.length > 0;
        // Ist die Firma selbst (ohne AP) aktiv?
        const companyActive = selected.includes(base.key) || (!hasPersons && selected.includes(base.key));
        const anyActive = companyActive || !!activeCp;

        return (
          <div key={base.key + idx} className="flex flex-wrap items-center gap-1.5">
            <button
              type="button"
              onClick={() => (anyActive ? setSel(new Set(selected.filter((k) => parseKey(k).contact_id !== base.contact_id))) : toggleKey(base.key))}
              data-testid={`recipient-chip-${base.key}`}
              className={`inline-flex items-center gap-1.5 h-7 px-2.5 rounded-full border text-[11px] font-medium transition-colors ${
                anyActive
                  ? "bg-slate-900 border-slate-900 text-white"
                  : "bg-white border-slate-200 text-slate-500 hover:border-slate-300 hover:text-slate-700"
              }`}
              title="Empfänger de-/aktivieren"
            >
              {iconFor(base.type, base.role)}
              <span>{base.label}</span>
            </button>

            {hasPersons && anyActive && (
              <>
                <select
                  value={activePersonId || ""}
                  onChange={(e) => setContactPerson(base.contact_id, e.target.value)}
                  data-testid={`recipient-cp-select-${base.contact_id}`}
                  className="h-7 rounded-md border border-slate-200 bg-white text-[11px] text-slate-700 px-2 max-w-[220px]"
                  title="Ansprechpartner für Anrede wählen"
                >
                  <option value="">— nur Firma (&bdquo;Damen und Herren&ldquo;) —</option>
                  {g.persons.map((p) => (
                    <option key={p.key} value={p.person_contact_id}>
                      {p.label}{p.is_primary ? " · primär" : ""}
                    </option>
                  ))}
                </select>
                {activePersonId && (
                  <label
                    className={`inline-flex items-center gap-1 h-7 px-2 rounded-full border text-[11px] cursor-pointer ${
                      zhdActive
                        ? "bg-emerald-50 border-emerald-300 text-emerald-900"
                        : "bg-white border-slate-200 text-slate-500 hover:text-slate-700"
                    }`}
                    title="Im Adressfenster »z. Hd. …« anzeigen"
                  >
                    <input
                      type="checkbox"
                      checked={zhdActive}
                      onChange={() => toggleZhd(base.contact_id)}
                      className="cursor-pointer"
                      data-testid={`recipient-zhd-${base.contact_id}`}
                    />
                    z. Hd.
                  </label>
                )}
              </>
            )}
          </div>
        );
      })}
    </div>
  );
}
